<template>
  <div>
    <div class="header">
      <div class="container-fluid">

        <b-row class="align-items-center mt-3 mb-2">
          <b-col>
            <span class="text-muted" v-if="condicionesFiltradas.length !== condiciones.length">
              {{ condicionesFiltradas.length }} / {{ condiciones.length }} {{ $t('cond_condiciones') }}
            </span>
            <span class="text-muted" v-else>{{ condiciones.length }} {{ $t('cond_condiciones') }}</span>
          </b-col>
          <b-col class="text-right">
            <button class="btn btn-sm btn-success mr-2" @click="exportarExcel" :disabled="!condicionesFiltradas.length" :title="$t('exportar_excel')">
              <i class="fas fa-file-excel mr-1" aria-hidden="true"></i>
              XLS
            </button>
            <router-link v-if="puedeEditar" :to="{ name: 'condicion-crear' }">
              <b-button variant="primary" size="sm">
                <i class="ni ni-fat-add mr-1"></i> {{ $t('cond_crear') }}
              </b-button>
            </router-link>
          </b-col>
        </b-row>

        <b-alert variant="danger" :show="!!error" dismissible @dismissed="error = ''">
          {{ error }}
        </b-alert>

        <div v-if="cargando" class="text-center text-muted mt-5">
          <b-spinner variant="primary" />
        </div>

        <div v-else-if="condiciones.length" class="card shadow" style="margin-bottom: 60px;">
          <div class="tabla-scroll-fixed" ref="tablaCondiciones">
            <table class="table table-hover align-items-center mb-0">
              <thead class="thead-sticky thead-light">
                <tr>
                  <th class="th-ordenable" @click="ordenarPor('nombre')">
                    <input class="th-search-input" v-model="filterCol.nombre" @click.stop :placeholder="$t('buscar')" />
                    <span class="th-label">{{ $t('nombre') }} <i :class="iconoOrden('nombre')"></i></span>
                  </th>
                  <th class="th-ordenable" @click="ordenarPor('nombreIngles')">
                    <span class="th-label">{{ $t('nombre_ingles') }} <i :class="iconoOrden('nombreIngles')"></i></span>
                  </th>
                  <th class="th-ordenable" @click="ordenarPor('codigo')">
                    <input class="th-search-input" v-model="filterCol.codigo" @click.stop :placeholder="$t('buscar')" />
                    <span class="th-label">{{ $t('codigo') }} <i :class="iconoOrden('codigo')"></i></span>
                  </th>
                  <th class="th-ordenable" @click="ordenarPor('observacion')">
                    <span class="th-label">{{ $t('observacion') }} <i :class="iconoOrden('observacion')"></i></span>
                  </th>
                  <th style="width:120px;"></th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="item in condicionesPaginadas" :key="item.id">
                  <td>{{ item.nombre }}</td>
                  <td>{{ item.nombreIngles || '-' }}</td>
                  <td>{{ item.codigo || '-' }}</td>
                  <td>{{ item.observacion || '-' }}</td>
                  <td class="text-center">
                    <router-link :to="{ name: 'condicion-detalle', params: { id: item.id } }">
                      <b-button size="sm" variant="outline-primary" class="mr-1">
                        <i class="fas fa-pencil-alt"></i>
                      </b-button>
                    </router-link>
                    <b-button v-if="puedeEditar" size="sm" variant="outline-danger" @click="eliminarCondicion(item.id)">
                      <i class="fas fa-trash"></i>
                    </b-button>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <div class="pagination-floating-bar" v-if="condiciones.length">
          <div class="d-flex align-items-center">
            <label class="mr-2 mb-0 text-secondary" style="font-size:.85rem;">{{ $t('items_por_pagina') }}</label>
            <select v-model.number="pageSize" @change="currentPage = 1" class="form-control form-control-sm" style="width:70px;">
              <option :value="20">20</option>
              <option :value="50">50</option>
              <option :value="100">100</option>
              <option :value="999999">{{ $t('todas') }}</option>
            </select>
          </div>
          <div class="d-flex align-items-center" style="gap:12px;">
            <button class="btn btn-outline-secondary btn-sm" :disabled="currentPage === 1" @click="currentPage--">
              <i class="fas fa-arrow-left mr-1"></i> {{ $t('anterior') }}
            </button>
            <span style="font-weight:600;color:#6c757d;font-size:.9rem;">
              {{ $t('pagina') }} {{ currentPage }} {{ $t('de') }} {{ totalPages }}
              <span class="text-muted ml-2" style="font-size:.8rem;">({{ condicionesFiltradas.length }} {{ $t('resultados') }})</span>
            </span>
            <button class="btn btn-outline-primary btn-sm" :disabled="currentPage >= totalPages" @click="currentPage++">
              {{ $t('siguiente') }} <i class="fas fa-arrow-right ml-1"></i>
            </button>
          </div>
          <div class="d-flex align-items-center" style="gap:6px;">
            <button class="btn btn-sm btn-scroll-nav" @click="scrollTabla('left')" :title="$t('scroll_izquierda')"><i class="fas fa-chevron-left"></i></button>
            <button class="btn btn-sm btn-scroll-nav" @click="scrollTabla('right')" :title="$t('scroll_derecha')"><i class="fas fa-chevron-right"></i></button>
            <button class="btn btn-sm btn-scroll-nav" @click="scrollTabla('top')" :title="$t('ir_al_inicio')"><i class="fas fa-chevron-up"></i></button>
            <button class="btn btn-sm btn-scroll-nav" @click="scrollTabla('bottom')" :title="$t('ir_al_final')"><i class="fas fa-chevron-down"></i></button>
          </div>
        </div>

        <div v-else-if="!cargando" class="text-center text-muted mt-5">
          <i class="fas fa-list-alt fa-3x mb-3"></i>
          <p>{{ $t('cond_no_hay') }}</p>
        </div>

      </div>
    </div>
  </div>
</template>

<script>
import condicionService from '@/api/condicionService';
import utilsService from '@/api/utilsService';
import Swal from 'sweetalert2';
import * as XLSX from 'xlsx';

export default {
  name: 'CondicionView',
  data() {
    return {
      condiciones: [],
      cargando: false,
      error: '',
      ordenColumna: null,
      ordenDireccion: 'asc',
      filterCol: {
        nombre: '',
        codigo: '',
      },
      currentPage: 1,
      pageSize: 50,
    };
  },

  watch: {
    filterCol: { deep: true, handler() { this.currentPage = 1; } },
  },

  mounted() {
    this.$store.dispatch('setSidebarCollapsed', false);
    this._scrollHandler = this._onWindowScroll.bind(this);
    window.addEventListener('scroll', this._scrollHandler, { passive: true });
    this.cargarCondiciones();
  },

  beforeDestroy() {
    this.$store.dispatch('setSidebarCollapsed', false);
    window.removeEventListener('scroll', this._scrollHandler);
  },

  computed: {
    puedeEditar() {
      return utilsService.puedeEscribir() && utilsService.getRol() !== 'ventas inex';
    },
    condicionesFiltradas() {
      const fc = this.filterCol;
      let lista = this.condiciones;
      if (fc.nombre) lista = lista.filter(c => (c.nombre || '').toLowerCase().includes(fc.nombre.toLowerCase()));
      if (fc.codigo) lista = lista.filter(c => (c.codigo || '').toLowerCase().includes(fc.codigo.toLowerCase()));
      return lista;
    },
    condicionesOrdenadas() {
      if (!this.ordenColumna) return this.condicionesFiltradas;
      const sorted = [...this.condicionesFiltradas];
      sorted.sort((a, b) => {
        let vA = a[this.ordenColumna];
        let vB = b[this.ordenColumna];
        if (vA == null) vA = '';
        if (vB == null) vB = '';
        if (typeof vA === 'number' && typeof vB === 'number') return this.ordenDireccion === 'asc' ? vA - vB : vB - vA;
        vA = String(vA).toLowerCase();
        vB = String(vB).toLowerCase();
        if (vA < vB) return this.ordenDireccion === 'asc' ? -1 : 1;
        if (vA > vB) return this.ordenDireccion === 'asc' ? 1 : -1;
        return 0;
      });
      return sorted;
    },
    totalPages() {
      if (this.pageSize >= 999999) return 1;
      return Math.max(1, Math.ceil(this.condicionesOrdenadas.length / this.pageSize));
    },
    condicionesPaginadas() {
      if (this.pageSize >= 999999) return this.condicionesOrdenadas;
      const start = (this.currentPage - 1) * this.pageSize;
      return this.condicionesOrdenadas.slice(start, start + this.pageSize);
    },
  },

  methods: {
    async cargarCondiciones() {
      this.cargando = true;
      this.error = '';
      try {
        this.condiciones = await condicionService.getAll();
      } catch (e) {
        this.error = (e && e.response && e.response.data && e.response.data.message) || this.$t('cond_error_cargar');
      } finally {
        this.cargando = false;
      }
    },

    eliminarCondicion(id) {
      Swal.fire({
        title: this.$t('esta_seguro'),
        text: this.$t('cond_eliminar_texto'),
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: this.$t('eliminar'),
        cancelButtonText: this.$t('cancelar'),
        confirmButtonColor: '#dc3545',
        cancelButtonColor: '#6c757d',
      }).then(async (result) => {
        if (result.isConfirmed) {
          try {
            await condicionService.deleteBy(id);
            this.condiciones = await condicionService.getAll();
            Swal.fire({ text: this.$t('eliminado_correctamente'), icon: 'success', timer: 2000, showConfirmButton: false });
          } catch (e) {
            this.error = this.$t('error_inesperado');
          }
        }
      });
    },

    ordenarPor(columna) {
      if (this.ordenColumna === columna) {
        this.ordenDireccion = this.ordenDireccion === 'asc' ? 'desc' : 'asc';
      } else {
        this.ordenColumna = columna;
        this.ordenDireccion = 'asc';
      }
      this.currentPage = 1;
    },

    iconoOrden(columna) {
      if (this.ordenColumna !== columna) return 'fas fa-sort text-muted';
      return this.ordenDireccion === 'asc' ? 'fas fa-sort-up' : 'fas fa-sort-down';
    },

    scrollTabla(dir) {
      const el = this.$refs.tablaCondiciones;
      const px = 300;
      if (dir === 'left')   el && el.scrollBy({ left: -px, behavior: 'smooth' });
      if (dir === 'right')  el && el.scrollBy({ left:  px, behavior: 'smooth' });
      if (dir === 'top')    window.scrollTo({ top: 0, behavior: 'smooth' });
      if (dir === 'bottom') window.scrollTo({ top: document.body.scrollHeight, behavior: 'smooth' });
    },

    _onWindowScroll() {
      const cont = this.$refs.tablaCondiciones;
      if (!cont) return;
      const thead = cont.querySelector('thead.thead-sticky');
      if (!thead) return;
      const top = cont.getBoundingClientRect().top;
      thead.style.transform = top < 0 ? `translateY(${-top}px)` : '';
    },

    exportarExcel() {
      const rows = this.condicionesOrdenadas;
      if (!rows.length) { Swal.fire({ icon: 'info', title: this.$t('no_filas_exportar') }); return; }

      const cols = [
        { key: 'nombre', label: this.$t('nombre') },
        { key: 'nombreIngles', label: this.$t('nombre_ingles') },
        { key: 'codigo', label: this.$t('codigo') },
        { key: 'observacion', label: this.$t('observacion') },
      ];
      const header = cols.map(c => c.label);
      const data = [header, ...rows.map(r => cols.map(c => r[c.key] == null ? '' : r[c.key]))];
      const ws = XLSX.utils.aoa_to_sheet(data);
      ws['!cols'] = header.map(h => ({ wch: Math.max(10, String(h).length + 2) }));
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Condiciones');
      const wbout = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
      const blob = new Blob([wbout], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });

      const fecha = new Date().toISOString().slice(0, 10);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Condiciones_${fecha}.xlsx`;
      document.body.appendChild(a);
      a.click();
      setTimeout(() => { document.body.removeChild(a); URL.revokeObjectURL(url); }, 0);
    },
  },
};
</script>

<style scoped>
.divisor {
  font-size: 16px;
  font-weight: 400;
  color: #fff;
  padding: 15px;
  background: #6172e9;
  border-radius: 10px;
}

.card {
  border-radius: 10px;
  overflow: hidden;
}

.tabla-scroll-fixed {
  overflow-x: auto;
  position: relative;
  font-size: 0.8rem;
}

.thead-sticky {
  position: relative;
  z-index: 2;
  background: #f8f9fa;
}

.th-ordenable {
  cursor: pointer;
  user-select: none;
  min-width: 90px;
  vertical-align: top;
  white-space: nowrap;
}
.th-ordenable:hover {
  background-color: #e9ecef !important;
}

.th-search-input {
  width: 100%;
  font-size: 11px;
  padding: 2px 4px;
  border: 1px solid #ced4da;
  border-radius: 3px;
  margin-bottom: 3px;
  display: block;
  font-weight: normal;
}

.th-label {
  display: block;
  font-size: 12px;
  font-weight: 600;
  white-space: nowrap;
}

.tabla-scroll-fixed .table th,
.tabla-scroll-fixed .table td {
  font-size: 12px;
}

.btn-scroll-nav {
  width: 32px;
  height: 32px;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px solid #ced4da;
  background: #f8f9fa;
  color: #495057;
  border-radius: 6px;
}
.btn-scroll-nav:hover {
  background: #e9ecef;
  border-color: #adb5bd;
}

.pagination-floating-bar {
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  z-index: 500;
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 24px;
  background: #fff;
  border-top: 1px solid #dee2e6;
  box-shadow: 0 -2px 8px rgba(0, 0, 0, 0.08);
}
</style>
